import { useState, useCallback } from 'react';
import { ZodError, z } from 'zod';
import type { ZodSchema } from 'zod';

const removeFalsyPropertiesFromErrors = obj => {
	return Object.keys(obj).reduce((acc, key) => {
		const value = obj[key];
		if (value && (!Array.isArray(value) || value.length > 0)) {
			acc[key] = value;
		}
		return acc;
	}, {});
};

export function useValidation<T>() {
	const [fieldErrors, setFieldErrors] = useState<Record<keyof T, string[]>>({} as Record<keyof T, string[]>);

	const validateField = useCallback((schema: ZodSchema<T>, field: keyof T, formState: T) => {
		const { success, error } = schema.safeParse(formState);
		if (success) {
			setFieldErrors(prev => ({ ...prev, [field]: undefined }));
		} else if (error instanceof ZodError) {
			const treeifiedError = z.treeifyError(error);
			const fieldError = ('properties' in treeifiedError && treeifiedError.properties?.[field as string]?.errors) || [];
			setFieldErrors(prev => ({ ...prev, [field]: fieldError }));
		}
	}, []);

	const clearFieldError = useCallback((field: keyof T) => {
		setFieldErrors(prev => ({ ...prev, [field]: undefined }));
	}, []);

	const validateAllSchema = useCallback((schema: ZodSchema<T>, formState: T) => {
		const { success, error } = schema.safeParse(formState);
		if (success) {
			setFieldErrors({} as Record<keyof T, string[]>);
		} else if (error instanceof ZodError) {
			const treeifiedError = z.treeifyError(error);
			const allFieldErrors =
				'properties' in treeifiedError && treeifiedError.properties
					? Object.entries(treeifiedError.properties).reduce(
							(acc, [key, value]) => {
								acc[key] = (value as any).errors;
								return acc;
							},
							{} as Record<string, string[]>,
						)
					: {};
			setFieldErrors(allFieldErrors as Record<keyof T, string[]>);
		}
	}, []);

	const errorsColection = Array.isArray(fieldErrors) ? fieldErrors.join('. ') : fieldErrors;
	const errors = removeFalsyPropertiesFromErrors(errorsColection);

	return {
		validateField,
		clearFieldError,
		validateAllSchema,
		fieldErrors: errors,
	};
}
